/**
 * Drizzle ORM schema — single source of truth for the D1 database.
 * Used by both api-worker and consumer-worker via the shared package.
 * To apply changes: pnpm db:generate  (creates migrations/drizzle/)
 *                   pnpm db:migrate:prod  (applies via drizzle-kit migrate)
 */
import { sqliteTable, text, integer, real, index, primaryKey } from 'drizzle-orm/sqlite-core';
import { sql } from 'drizzle-orm';

// ─────────────────────────────────────────────
// import_jobs
// ─────────────────────────────────────────────
export const importJobs = sqliteTable(
  'import_jobs',
  {
    id: text('id').primaryKey(), // ULID
    userId: text('user_id').notNull(), // Clerk user_id
    taxYear: integer('tax_year').notNull(), // e.g. 2024
    source: text('source').notNull(), // 'binance' | 'trading212'
    status: text('status').notNull().default('pending'),
    // pending | uploading | queued | processing | done | error
    r2Key: text('r2_key'),
    errorMsg: text('error_msg'),
    rowCount: integer('row_count'),
    createdAt: text('created_at')
      .notNull()
      .default(sql`(datetime('now'))`),
    updatedAt: text('updated_at')
      .notNull()
      .default(sql`(datetime('now'))`),
  },
  (table) => [
    index('idx_jobs_user').on(table.userId),
    index('idx_jobs_year').on(table.userId, table.taxYear),
  ],
);

export type ImportJobRow = typeof importJobs.$inferSelect;
export type NewImportJob = typeof importJobs.$inferInsert;

// ─────────────────────────────────────────────
// audit_rows
// ─────────────────────────────────────────────
export const auditRows = sqliteTable(
  'audit_rows',
  {
    id: text('id').primaryKey(), // ULID
    jobId: text('job_id')
      .notNull()
      .references(() => importJobs.id),
    rowIndex: integer('row_index').notNull(), // 0-based CSV row
    rawSnapshot: text('raw_snapshot').notNull(), // JSON of original CSV columns
    eventType: text('event_type').notNull(),
    // 'equity_buy' | 'equity_sell' | 'dividend' | 'crypto_buy' | 'crypto_sell_fiat' | 'crypto_swap' | 'skip'
    eventDate: text('event_date'), // ISO YYYY-MM-DD
    asset: text('asset'),
    quantity: real('quantity'),
    priceOrig: real('price_orig'), // price in original currency
    currencyOrig: text('currency_orig'),
    amountOrig: real('amount_orig'), // qty * price, before FX
    fxDate: text('fx_date'), // NBP rate date used
    fxRate: real('fx_rate'), // PLN per 1 unit of currency_orig
    amountPln: real('amount_pln'), // amount_orig * fx_rate
    pitBox: text('pit_box'),
    // 'pos22_revenue' | 'pos23_cost' | 'pos36_revenue' | 'pos39_cost' | null
    skipReason: text('skip_reason'),
  },
  (table) => [index('idx_audit_job').on(table.jobId)],
);

export type AuditRowDb = typeof auditRows.$inferSelect;
export type NewAuditRow = typeof auditRows.$inferInsert;

// ─────────────────────────────────────────────
// tax_totals
// ─────────────────────────────────────────────
export const taxTotals = sqliteTable('tax_totals', {
  jobId: text('job_id')
    .primaryKey()
    .references(() => importJobs.id),
  // Part C: equities / other securities
  pos22Revenue: real('pos22_revenue').notNull().default(0),
  pos23Cost: real('pos23_cost').notNull().default(0),
  // Part E: virtual currencies (crypto)
  pos36Revenue: real('pos36_revenue').notNull().default(0),
  pos37Cost: real('pos37_cost').notNull().default(0),
  // pos38 / pos39 are computed at query time: MAX(0, revenue - cost)
  computedAt: text('computed_at')
    .notNull()
    .default(sql`(datetime('now'))`),
});

export type TaxTotalsRow = typeof taxTotals.$inferSelect;
export type NewTaxTotals = typeof taxTotals.$inferInsert;

// ─────────────────────────────────────────────
// fx_rates  (FX rate cache — source-agnostic)
// ─────────────────────────────────────────────
export const fxRates = sqliteTable(
  'fx_rates',
  {
    rateDate: text('rate_date').notNull(), // YYYY-MM-DD (actual publication date)
    currency: text('currency').notNull(), // ISO 4217 or broker code, e.g. 'USD', 'GBX'
    ratePln: real('rate_pln').notNull(), // mid rate: PLN per 1 foreign unit
    source: text('source').notNull(), // e.g. 'NBP/023/A/NBP/2025' or 'STATIC'
  },
  (table) => [primaryKey({ columns: [table.rateDate, table.currency] })],
);

export type FxRateRow = typeof fxRates.$inferSelect;
export type NewFxRate = typeof fxRates.$inferInsert;

// ─────────────────────────────────────────────
// exchanges  (Admin-managed exchange catalog)
// ─────────────────────────────────────────────
export const exchanges = sqliteTable('exchanges', {
  id: text('id').primaryKey(), // ULID
  slug: text('slug').notNull().unique(), // 'binance' | 'trading212' | etc.
  name: text('name').notNull(), // 'Binance', 'Trading 212'
  logoUrl: text('logo_url'),
  apiDocUrl: text('api_doc_url'),
  // JSON array of required credential field names: ["api_key","api_secret"]
  requiredCredentials: text('required_credentials').notNull(),
  isActive: integer('is_active').notNull().default(1),
  createdAt: text('created_at')
    .notNull()
    .default(sql`(datetime('now'))`),
  updatedAt: text('updated_at')
    .notNull()
    .default(sql`(datetime('now'))`),
});

export type ExchangeRow = typeof exchanges.$inferSelect;
export type NewExchange = typeof exchanges.$inferInsert;

// ─────────────────────────────────────────────
// user_exchanges  (User-connected exchange accounts)
// ─────────────────────────────────────────────
export const userExchanges = sqliteTable(
  'user_exchanges',
  {
    id: text('id').primaryKey(), // ULID
    userId: text('user_id').notNull(), // Clerk user_id
    exchangeId: text('exchange_id')
      .notNull()
      .references(() => exchanges.id),
    label: text('label'), // User-defined label, e.g. "Binance Main"
    // Sync state
    lastSyncedAt: text('last_synced_at'),
    syncStatus: text('sync_status').notNull().default('never'),
    // 'never' | 'syncing' | 'ok' | 'error'
    syncErrorMsg: text('sync_error_msg'),
    // Exchange-provided account totals (e.g. Trading 212 account/summary).
    // When present these are used instead of per-position NBP-based summation
    // so dashboard values match the exchange's own interbank-rate calculations.
    nativeAccountValue: real('native_account_value'),
    nativeAccountCost: real('native_account_cost'),
    nativeAccountCurrency: text('native_account_currency'),
    createdAt: text('created_at')
      .notNull()
      .default(sql`(datetime('now'))`),
    updatedAt: text('updated_at')
      .notNull()
      .default(sql`(datetime('now'))`),
  },
  (table) => [
    index('idx_user_exchanges_user').on(table.userId),
    index('idx_user_exchanges_unique').on(table.userId, table.exchangeId, table.label),
  ],
);

export type UserExchangeRow = typeof userExchanges.$inferSelect;
export type NewUserExchange = typeof userExchanges.$inferInsert;

// ─────────────────────────────────────────────
// sync_jobs  (Portfolio synchronisation jobs)
// ─────────────────────────────────────────────
export const syncJobs = sqliteTable('sync_jobs', {
  id: text('id').primaryKey(), // ULID
  userExchangeId: text('user_exchange_id')
    .notNull()
    .references(() => userExchanges.id),
  userId: text('user_id').notNull(),
  status: text('status').notNull().default('queued'),
  // 'queued' | 'processing' | 'done' | 'error'
  errorMsg: text('error_msg'),
  rowsProcessed: integer('rows_processed'),
  startedAt: text('started_at'),
  completedAt: text('completed_at'),
  createdAt: text('created_at')
    .notNull()
    .default(sql`(datetime('now'))`),
});

export type SyncJobRow = typeof syncJobs.$inferSelect;
export type NewSyncJob = typeof syncJobs.$inferInsert;

// ─────────────────────────────────────────────
// portfolio_positions  (Latest sync results)
// ─────────────────────────────────────────────
export const portfolioPositions = sqliteTable(
  'portfolio_positions',
  {
    id: text('id').primaryKey(), // ULID
    userId: text('user_id').notNull(),
    userExchangeId: text('user_exchange_id')
      .notNull()
      .references(() => userExchanges.id),
    assetSymbol: text('asset_symbol').notNull(), // 'BTC', 'ETH', 'AAPL'
    assetType: text('asset_type').notNull(), // 'crypto' | 'equity' | 'etf' | 'cash'
    quantity: real('quantity').notNull(),
    avgBuyPrice: real('avg_buy_price'), // Weighted avg cost (original currency)
    currency: text('currency').notNull(), // 'USD', 'EUR', 'PLN'
    lastPrice: real('last_price'),
    lastPriceAt: text('last_price_at'),
    createdAt: text('created_at')
      .notNull()
      .default(sql`(datetime('now'))`),
    updatedAt: text('updated_at')
      .notNull()
      .default(sql`(datetime('now'))`),
  },
  (table) => [
    index('idx_portfolio_user').on(table.userId),
    index('idx_portfolio_unique').on(table.userId, table.userExchangeId, table.assetSymbol),
  ],
);

export type PortfolioPositionRow = typeof portfolioPositions.$inferSelect;
export type NewPortfolioPosition = typeof portfolioPositions.$inferInsert;

// ─────────────────────────────────────────────
// user_profiles  (User preferences)
// ─────────────────────────────────────────────
export const userProfiles = sqliteTable('user_profiles', {
  userId: text('user_id').primaryKey(), // Clerk user_id
  preferredCurrency: text('preferred_currency').notNull().default('USD'),
  updatedAt: text('updated_at')
    .notNull()
    .default(sql`(datetime('now'))`),
});

export type UserProfileRow = typeof userProfiles.$inferSelect;
export type NewUserProfile = typeof userProfiles.$inferInsert;

// ─────────────────────────────────────────────
// feature_flags  (Global admin-controlled toggles)
// ─────────────────────────────────────────────
export const featureFlags = sqliteTable('feature_flags', {
  key: text('key').primaryKey(), // 'yahoo_ticker_live_pricing'
  isEnabled: integer('is_enabled').notNull().default(0), // 0 | 1
  description: text('description'),
  updatedBy: text('updated_by'), // Clerk user_id of admin who last changed it
  updatedAt: text('updated_at')
    .notNull()
    .default(sql`(datetime('now'))`),
});

export type FeatureFlagRow = typeof featureFlags.$inferSelect;
export type NewFeatureFlag = typeof featureFlags.$inferInsert;
